import { useCallback, useMemo, useState } from 'react';
import {
  useExternalOrders,
  useExternalOrder,
  useCreateExternalOrder,
  useCheckExternalOrderStock,
  useProcessExternalOrder,
} from '../../../hooks/useExternalOrders';
import { useStockTransfers } from '../../../hooks/useStockTransfers';
import { useWarehouses, useWarehouse } from '../../../hooks/useWarehouses';
import { useProjects, useProject } from '../../../hooks/useProjects';
import { ITEMS_PER_PAGE, getInitialOrderState } from '../constants/internalOrdersConstants';
import {
  filterInternalOrders,
  buildCreateOrderPayload,
  mapExternalOrderToDisplay,
  findOpenTransferForOrder,
} from '../utils/internalOrdersUtils';
import { usePaginatedFilter } from '../../../common/hooks/usePaginatedFilter';

export const useInternalOrdersTable = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [receiveDialogOpen, setReceiveDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(getInitialOrderState);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [selectedTransfer, setSelectedTransfer] = useState(null);

  const createModalOpen = dialogOpen;

  const { data: orders = [], isLoading: ordersLoading } = useExternalOrders();
  const { data: detailOrder, isLoading: detailOrderLoading, refetch: refetchDetailOrder } = useExternalOrder(
    selectedOrderId,
    {
      enabled: detailDialogOpen && Boolean(selectedOrderId),
    }
  );
  const { data: detailProject } = useProject(detailOrder?.uuid_project, {
    enabled: detailDialogOpen && Boolean(detailOrder?.uuid_project),
  });
  const { data: detailWarehouse } = useWarehouse(
    detailOrder?.uuid_destination_warehouse,
    {
      enabled: detailDialogOpen && Boolean(detailOrder?.uuid_destination_warehouse),
    }
  );
  const { data: transfers = [] } = useStockTransfers(undefined, {
    enabled: detailDialogOpen || receiveDialogOpen,
  });
  const { data: warehouses = [], isLoading: warehousesLoading } = useWarehouses({
    enabled: createModalOpen,
  });
  const { data: projects = [], isLoading: projectsLoading } = useProjects({
    enabled: createModalOpen,
  });

  const createOrderMutation = useCreateExternalOrder();
  const checkStockMutation = useCheckExternalOrderStock();
  const processOrderMutation = useProcessExternalOrder();

  const displayOrders = useMemo(
    () => orders.map((order) => mapExternalOrderToDisplay(order, [], [])),
    [orders]
  );

  const {
    data,
    totalCount,
    totalPages,
    page,
    rowsPerPage,
    searchTerm,
    handleSearch,
    handlePageChange,
    handleRowsPerPageChange,
  } = usePaginatedFilter({
    data: displayOrders,
    itemsPerPage: ITEMS_PER_PAGE,
    filterFn: filterInternalOrders,
  });

  const selectedOrderDetail = useMemo(() => {
    if (!detailDialogOpen || !selectedOrderId || !detailOrder) return null;

    return mapExternalOrderToDisplay(detailOrder, [], [], {
      warehouse: detailWarehouse,
      project: detailProject,
    });
  }, [
    detailDialogOpen,
    selectedOrderId,
    detailOrder,
    detailWarehouse,
    detailProject,
  ]);

  const selectedOrderTransfer = useMemo(() => {
    if (!selectedOrderId) return null;
    return findOpenTransferForOrder(transfers, selectedOrderId);
  }, [transfers, selectedOrderId]);

  const isLoading = ordersLoading;
  const isSaving = createOrderMutation.isPending;
  const isProcessing =
    checkStockMutation.isPending || processOrderMutation.isPending;

  const handleOpenDialog = useCallback(() => {
    setSelectedOrder(getInitialOrderState());
    setDialogOpen(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setDialogOpen(false);
    setSelectedOrder(getInitialOrderState());
  }, []);

  const handleInputChange = useCallback((event) => {
    const { name, value } = event.target;
    setSelectedOrder((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const handleSelectChange = useCallback((event) => {
    const { name, value } = event.target;
    setSelectedOrder((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  const handleAddMaterial = useCallback((material) => {
    if (!material?.value) return;

    setSelectedOrder((prev) => {
      const exists = (prev.materials || []).some(
        (item) => item.materialId === material.value
      );
      if (exists) return prev;

      return {
        ...prev,
        materials: [
          ...(prev.materials || []),
          {
            materialId: material.value,
            code: material.code,
            name: material.name,
            unit: material.unit,
            quantity: 1,
          },
        ],
      };
    });
  }, []);

  const handleUpdateMaterialQuantity = useCallback((materialId, quantity) => {
    setSelectedOrder((prev) => ({
      ...prev,
      materials: (prev.materials || []).map((item) =>
        item.materialId === materialId
          ? { ...item, quantity: quantity === '' ? '' : Number(quantity) }
          : item
      ),
    }));
  }, []);

  const handleRemoveMaterial = useCallback((materialId) => {
    setSelectedOrder((prev) => ({
      ...prev,
      materials: (prev.materials || []).filter(
        (item) => item.materialId !== materialId
      ),
    }));
  }, []);

  const handleSave = useCallback(async () => {
    const payload = buildCreateOrderPayload(selectedOrder);
    if (!payload) return false;

    await createOrderMutation.mutateAsync(payload);
    handleCloseDialog();
    return true;
  }, [selectedOrder, createOrderMutation, handleCloseDialog]);

  const handleViewDetail = useCallback((order) => {
    setSelectedOrderId(order.uuid);
    setDetailDialogOpen(true);
  }, []);

  const handleCloseDetailDialog = useCallback(() => {
    setDetailDialogOpen(false);
    setSelectedOrderId(null);
  }, []);

  const handleCheckStock = useCallback(async (orderId) => {
    return checkStockMutation.mutateAsync(orderId);
  }, [checkStockMutation]);

  const handleProcessOrder = useCallback(async (orderId) => {
    return processOrderMutation.mutateAsync(orderId);
  }, [processOrderMutation]);

  const handleOpenReceiveDialog = useCallback((transfer) => {
    setSelectedTransfer(transfer);
    setReceiveDialogOpen(true);
  }, []);

  const handleCloseReceiveDialog = useCallback(() => {
    setReceiveDialogOpen(false);
    setSelectedTransfer(null);
  }, []);

  const handleRefreshDetailOrder = useCallback(async () => {
    await refetchDetailOrder();
  }, [refetchDetailOrder]);

  return {
    data,
    isLoading,
    isSaving,
    isProcessing,
    page,
    totalPages,
    totalCount,
    rowsPerPage,
    searchTerm,
    dialogOpen,
    detailDialogOpen,
    receiveDialogOpen,
    selectedOrder,
    selectedOrderDetail,
    selectedOrderTransfer,
    selectedTransfer,
    detailOrderLoading,
    warehouses,
    projects,
    projectsLoading,
    warehousesLoading,
    handleSearch,
    handlePageChange,
    handleRowsPerPageChange,
    handleOpenDialog,
    handleCloseDialog,
    handleInputChange,
    handleSelectChange,
    handleAddMaterial,
    handleUpdateMaterialQuantity,
    handleRemoveMaterial,
    handleSave,
    handleViewDetail,
    handleCloseDetailDialog,
    handleCheckStock,
    handleProcessOrder,
    handleOpenReceiveDialog,
    handleCloseReceiveDialog,
    handleRefreshDetailOrder,
  };
};
